import moment, { Moment } from "moment";
import { makeAutoObservable } from "mobx";
import { IDateEntry, Optional } from "@lib/types";
import { v4 as uuidv4 } from "uuid";

const numExp = /^[+-]?\d+(\.\d+)?$/;

// todo: remove in types

type IItemTaskId = {
    itemId: number;
    path: string;
    taskId: number;
};

type ISchedular = {
  id: string;
  name: string;
  EnumTypeRef: string;
  ExceptionInput: IItemTaskId;
  ExceptionOutput: IItemTaskId;
  WeeklyScheduleInput: IItemTaskId;
  WeeklyScheduleOutput: IItemTaskId;
};

export enum ECalendarType {
  today = "today",
  day = "day",
  week = "week",
  workweek = "workweek",
  month = "month",
}

export enum ECalendarRool {
  null = "null",
  boolean = "boolean",
  enumerate = "enumerate",
}

export interface ICalendarRoolTime {
  time: number;
  value: boolean;
}

export type ICalendarRool = {
  name: string;
  period: number;
  dataType: ECalendarRool;
  transfer: Array<number>;
  time: Array<ICalendarRoolTime>;
  comment: string;
  archive: boolean;
  message: string;
};

type ICalendarRemove = {
  name: string;
  period: number;
  dataType: boolean;
  time: Array<ICalendarRoolTime>;
  comment: string;
  archive: boolean;
  message: string;
  weekDay: string;
  monthDay: string;
  month: string;
  year: string;
};

export enum EWeek {
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
  Sunday,
}

export type ITime = {
  hours: string;
  minutes: string;
  seconds: string;
  hundredth: string;
};

export interface IDateException {
  dayOfWeek?: string; // 1-7 (Monday-Sunday), * (unspecified)
  dayOfMonth?: string; // 01-31, 32 (last day), 33 (odd days), 34 (even days), ** (any)
  month?: string; // 01-12, 13 (odd months), 14 (even months), ** (any)
  year?: string; // 1900-2255, **** (any)
}

export interface IExclude {
  id: string;
  period: IDateEntry;
  priority: number;
  time: ITime;
  dataType: number;
  length: number;
  value: string | number;
}

export type IEvent = {
  id: string;
  time: ITime;
  dataType: number;
  length: number;
  value: string | number;
};

export type IWeeklyState = {
  [T in EWeek]: Optional<IEvent[]>;
};

export type ICalendarStore = {
  date: Moment;
  mode: ECalendarType;
  weekModal: ICalendarRool;
  excludeModal: ICalendarRemove;
  calendars: Array<ISchedular>;
  calendarID?: string;
  selectedCalendar?: ISchedular;
  weeklySchedule?: IWeeklyState;
  excludeSchedule: Array<IExclude>;
};

export class CalendarStore implements ICalendarStore {
  id: string;
  width: number = 1100;
  height: number = 664;
  date: Moment = moment();
  mode: ECalendarType = ECalendarType.day;
  calendarID?: string = undefined;
  weeklySchedule: IWeeklyState = {
    [EWeek.Monday]: [],
    [EWeek.Tuesday]: [],
    [EWeek.Wednesday]: [],
    [EWeek.Thursday]: [],
    [EWeek.Friday]: [],
    [EWeek.Saturday]: [],
    [EWeek.Sunday]: [],
  };
  excludeSchedule: Array<IExclude> = [];

  calendars: Array<ISchedular> = [];

  weekModal: ICalendarRool = {
    name: "",
    period: 0,
    dataType: ECalendarRool.null,
    transfer: [],
    time: [],
    comment: "",
    archive: false,
    message: "",
  };

  excludeModal: ICalendarRemove = {
    name: "",
    period: 0,
    dataType: false,
    time: [],
    comment: "",
    archive: false,
    message: "",
    weekDay: "",
    monthDay: "",
    month: "",
    year: "",
  };

  constructor(id: string) {
    this.id = id;

    makeAutoObservable(this);
  }

  get selectedCalendar(): ISchedular | undefined {
    if (!this.calendarID) return undefined;
    return this.calendars.find((v) => v.id === this.calendarID);
  }

  setAttribute = (name: string, value: string) => {
    let res: any = value;
    if (value === "true") res = true;
    if (value === "false") res = false;
    if (numExp.test(value)) res = parseFloat(value);
    this[name as keyof Omit<ICalendarStore, "selectedCalendar">] = res as never;
  };

  onChange = <T extends keyof ICalendarStore>(
    name: T,
    value: ICalendarStore[T]
  ) => {
    this[name] = value as never;
  };

  setTime(data: Array<ICalendarRoolTime>) {
    this.weekModal.time = data;
  }

  setTransfer(data: Array<number>) {
    this.weekModal.transfer = data;
  }
}

export class CalendarsStore {
  values: { [key: string]: CalendarStore } = {};

  constructor() {
    makeAutoObservable(this);
  }

  add = () => {
    const id = uuidv4();
    const state = new CalendarStore(id);
    this.values[id] = state;
    return { id, state };
  };

  find = (id: string) => this.values[id];
}
